import React, { useEffect, useState } from 'react';
import { CSpinner } from '@coreui/react';
import dayjs from 'dayjs';
import { callTokenApi } from '../../../../../../apiCaller';
import { toast } from 'react-toastify';
import { capitalizeFirstLetter } from '../../../../../../utils';
import { API_AI_INSIGHTS_JOB_RERUNS } from '../../../../../../constants/apiRest';
import ViewMarkdown from '../Components/ViewMarkdown';
import Accordion from '../../../../../general/accordion';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

const ReviewPrompt = ({ insight }) => {
    const [loading, setLoading] = useState(false);
    const [jobChain, setJobChain] = useState([]);
    const [expandedJobId, setExpandedJobId] = useState(null);

    useEffect(() => {
        if (insight?.id) {
            fetchJobChain(insight.id);
        }
    }, [insight]);

    const fetchJobChain = async (id) => {
        setLoading(true);
        try {
            const response = await callTokenApi(API_AI_INSIGHTS_JOB_RERUNS.replace(':jobId', id), 'GET');

            if (response?.data?.code === 200) {
                const chain = response.data.data || [];
                setJobChain(chain);
                // Default to expanding the latest job (last in array since sorted by rerunNumber asc)
                if (chain.length > 0) {
                    setExpandedJobId(chain[chain.length - 1].id);
                }
            } else {
                toast.error('Failed to load insight details');
            }
        } catch {
            toast.error('An error occurred while loading insight details');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return dateString ? dayjs.utc(dateString).format('MMM D, YYYY') : 'N/A';
    };

    // Format email recipients as a comma-separated string
    const formatRecipients = (recipients) => {
        if (!recipients || recipients.length === 0) return 'None';

        // Handle string format by parsing to JSON
        if (typeof recipients === 'string') {
            try {
                recipients = JSON.parse(recipients);
            } catch (e) {
                return recipients;
            }
        }

        // Handle array of objects with label and value properties
        if (Array.isArray(recipients) && recipients.length > 0) {
            if (recipients[0] && (recipients[0].label || recipients[0].value)) {
                return recipients.map((recipient) => recipient.label || recipient.value).join(', ');
            }
            // Handle simple array of strings
            return recipients.join(', ');
        }

        // Fallback for any other format
        return String(recipients);
    };

    // Use the first job in chain (original) for display metadata, fallback to insight prop
    const displayData = jobChain.length > 0 ? jobChain[0] : insight;

    if (loading) {
        return (
            <div className='rule-detail account-connected-detail review-analyse'>
                <div className='cvr-create-new text-center py-5'>
                    <CSpinner color='primary' />
                    <p className='mt-3'>Loading insight details...</p>
                </div>
            </div>
        );
    }

    if (!displayData) {
        return (
            <div className='rule-detail account-connected-detail review-analyse'>
                <div className='cvr-create-new text-center py-5'>
                    <p>No insight data available.</p>
                </div>
            </div>
        );
    }

    return (
        <div className='rule-detail account-connected-detail review-analyse'>
            <div className='cvr-create-new'>
                <h1 className='mb-3'>View Prompt</h1>

                <ul className='d-flex flex-column gap-1 list-unstyled my-4'>
                    <li className='d-flex gap-2'>
                        <strong>AI Insights Template:</strong>
                        <span>{displayData.templateName || 'N/A'}</span>
                    </li>

                    {displayData.scheduleName && (
                        <li className='d-flex gap-2'>
                            <strong>Schedule:</strong>
                            <span>{displayData.scheduleName || 'N/A'}</span>
                        </li>
                    )}

                    <li className='d-flex gap-2'>
                        <strong>Generated on:</strong>
                        <span>{formatDate(displayData.createdAt)}</span>
                    </li>

                    <li className='d-flex gap-2'>
                        <strong>Completed on:</strong>
                        <span>{formatDate(displayData.completedAt)}</span>
                    </li>

                    <li className='d-flex gap-2'>
                        <strong>Status:</strong>
                        <span>{capitalizeFirstLetter(displayData.status) || 'N/A'}</span>
                    </li>

                    <li className='d-flex gap-2'>
                        <strong>Email to:</strong>
                        <span>{formatRecipients(displayData.emailedRecipients)}</span>
                    </li>

                    <li className='d-flex gap-2'>
                        <strong>Email Status:</strong>
                        <span>{capitalizeFirstLetter(displayData.emailDeliveryStatus) || 'Not sent'}</span>
                    </li>
                </ul>

                <div className='insight-content'>
                    {/* Job Chain Accordion - show only if there are jobs */}
                    {jobChain.length > 0 ? (
                        <div className='job-chain-accordion'>
                            {jobChain.map((job) => {
                                const jobLabel = !job.parentJobId ? 'Original' : `Rerun #${job.rerunNumber}`;
                                const jobDate = formatDate(job.completedAt || job.createdAt);
                                const jobStatus = job.status ? capitalizeFirstLetter(job.status) : '';

                                return (
                                    <div key={job.id} className='accordion'>
                                        <Accordion
                                            accordionType={job.id}
                                            title={`${jobLabel} - ${jobStatus}`}
                                            description={jobDate}
                                            accordionShow={expandedJobId}
                                            setAccordionShow={setExpandedJobId}
                                            className='job-chain-item'
                                        >
                                            {job.executedPrompt ? (
                                                <ViewMarkdown markdown={job.executedPrompt} isCsv={true} />
                                            ) : (
                                                <p className='text-muted mb-0'>No prompt available for this insight.</p>
                                            )}
                                        </Accordion>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        // Fallback to displayData when no job chain
                        displayData.executedPrompt ? (
                            <ViewMarkdown markdown={displayData.executedPrompt} isCsv={true} />
                        ) : (
                            <p className='text-muted'>No content available for this insight.</p>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReviewPrompt;
